import { useEffect, useState } from "react";
import { UserPlus, Trash2, Loader2, ShieldCheck, Shield, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RevealEmail } from "@/components/RevealEmail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminStaff() {
  const [staff, setStaff] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", permissions: [] });
  const [editing, setEditing] = useState(null); // staff object being edited
  const [editForm, setEditForm] = useState({ role: "staff", permissions: [] });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/staff"), api.get("/admin/permissions")])
      .then(([sRes, pRes]) => {
        setStaff(sRes.data);
        setPerms(pRes.data);
      })
      .catch(() => toast.error("Failed to load staff"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const togglePerm = (list, key) =>
    list.includes(key) ? list.filter((p) => p !== key) : [...list, key];

  const addStaff = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email, and a 6+ character password are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff member added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff", permissions: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({ role: s.role, permissions: s.permissions || [] });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/admin/staff/${editing.id}`, editForm);
      toast.success("Access updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const removeStaff = async (id) => {
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Staff member removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900">
            Staff &amp; Administrators
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            People with access to the Finance &amp; Tax Office dashboard.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="add-staff-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={addStaff} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="staff-name-input"
                  placeholder="Jane Officer"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="staff-email-input"
                  placeholder="jane@cheltenham.gov"
                />
              </div>
              <div className="space-y-2">
                <Label>Temporary password</Label>
                <Input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="staff-password-input"
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Administrator (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "staff" && (
                <div className="space-y-2">
                  <Label>Access permissions</Label>
                  <div className="grid gap-2 border border-slate-200 rounded-md p-3">
                    {perms.map((p) => (
                      <label key={p.key} className="flex items-center gap-3 cursor-pointer text-sm">
                        <Checkbox
                          checked={form.permissions.includes(p.key)}
                          onCheckedChange={() => setForm({ ...form, permissions: togglePerm(form.permissions, p.key) })}
                          data-testid={`perm-add-${p.key}`}
                        />
                        <span className="text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full"
                  data-testid="staff-submit-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create account
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="staff-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium hidden sm:table-cell">Email</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staff.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`staff-row-${s.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                        {s.role === "admin" ? (
                          <ShieldCheck className="w-4 h-4 text-[#1E3A8A]" />
                        ) : (
                          <Shield className="w-4 h-4 text-[#1E3A8A]" />
                        )}
                      </span>
                      <div>
                        <p className="font-medium text-slate-900">{s.name}</p>
                        {s.is_primary && (
                          <span className="text-xs text-emerald-700">Primary administrator</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">
                    <RevealEmail
                      masked={s.email}
                      testId={`staff-email-${s.id}`}
                      onReveal={async () => {
                        const { data } = await api.get(`/admin/staff/${s.id}/email`);
                        return data.email;
                      }}
                    />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1.5">
                      <Badge
                        className={
                          s.role === "admin"
                            ? "bg-blue-50 text-[#1E3A8A] border-blue-200 hover:bg-blue-50 w-fit"
                            : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100 w-fit"
                        }
                      >
                        {s.role}
                      </Badge>
                      {s.role === "staff" && (
                        <span className="text-xs text-slate-400">
                          {s.permissions && s.permissions.length
                            ? `${s.permissions.length} permission(s)`
                            : "No permissions"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {s.is_primary ? (
                      <span className="text-xs text-slate-400">Protected</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-300"
                          onClick={() => openEdit(s)}
                          data-testid={`edit-staff-${s.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!s.is_self && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-slate-300 text-red-700 hover:bg-red-50"
                                data-testid={`remove-staff-${s.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  They will lose access to the admin dashboard immediately. This
                                  cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => removeStaff(s.id)}
                                  className="bg-red-700 hover:bg-red-800"
                                  data-testid={`confirm-remove-${s.id}`}
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit access — {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Administrator (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "staff" && (
              <div className="space-y-2">
                <Label>Access permissions</Label>
                <div className="grid gap-2 border border-slate-200 rounded-md p-3">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-3 cursor-pointer text-sm">
                      <Checkbox
                        checked={editForm.permissions.includes(p.key)}
                        onCheckedChange={() => setEditForm({ ...editForm, permissions: togglePerm(editForm.permissions, p.key) })}
                        data-testid={`perm-edit-${p.key}`}
                      />
                      <span className="text-slate-700">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={saveEdit}
                disabled={savingEdit}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full"
                data-testid="edit-staff-submit"
              >
                {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save access
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
